<template>
    <CreateEditData 
        :model="'team'"
        :form="form"
        :similarData="similarTeams"
        :loading="loading"
        backTo="/team"
        @fetchData="getTeam">
    <template #headerActions>
        <div class="flex items-center gap-2">
            <Tooltip :text="$t('form.delete')">
                <Button @click="confirmDelete" type="danger" :disabled="!teamId" class="!m-0 !p-2 flex items-center justify-center">
                    <TrashIcon class="w-5 h-5" />
                </Button>
            </Tooltip>
            <Tooltip :text="$t('form.reset')">
                <Button @click="resetForm" type="secondary" class="!m-0 !p-2 flex items-center justify-center">
                    <ArrowPathIcon class="w-5 h-5 text-gray-500" />
                </Button>
            </Tooltip>
            <Tooltip :text="$t('tour.startTour')">
                <Button @click="() => startTeamFormTour(true)" type="secondary" class="!m-0 !p-2 flex items-center justify-center text-indigo-500">
                    <QuestionMarkCircleIcon class="w-5 h-5" />
                </Button>
            </Tooltip>
            <Button data-tour="team-save-btn" @click="handleSubmit" type="clear" :disabled="!validateForm()" class="!m-0">
                {{ $t('form.save') }}
            </Button>
        </div>
    </template>
            <form @submit.prevent="handleSubmit" class="">
                <!-- Title -->
                <FormContainer data-tour="team-name">
                    <Label :label="'Name'" />
                    <Input
                        id="name"
                        v-model="form.name"
                        type="text"
                        required
                        placeholder="Enter team name"
                        @input="searchSimilarTeams"
                    />
                </FormContainer>
                <FormContainer data-tour="team-description">
                    <Label :label="'Description'" />
                    <TextArea
                        id="description"
                        v-model="form.description"
                        type="text"
                        placeholder="Write the team's description"
                        class="h-[200px]"
                    />
                </FormContainer>
                <!-- Parent Team Selector -->
                <FormContainer data-tour="team-parent">
                    <Label :label="$t('form.parentTeam')" />
                    <select
                        id="parentTeam"
                        v-model="form.parentId"
                        class="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white rounded-lg p-2.5 text-sm focus:ring-primary-500 focus:border-primary-500"
                    >
                        <option :value="null">{{ $t('form.noParentTeam') }}</option>
                        <option 
                            v-for="availableParent in availableParentTeams" 
                            :key="availableParent.id || availableParent._id" 
                            :value="availableParent.id || availableParent._id"
                        >
                            {{ availableParent.name }}
                        </option>
                    </select>
                </FormContainer>
                <FormContainer v-if="teamId && subgroups.length > 0">
                    <Label :label="$t('form.subgroups')" />
                    <div class="space-y-2">
                        <div 
                            v-for="sub in subgroups" 
                            :key="sub.id || sub._id"
                            class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                        >
                            <div>
                                <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ sub.name }}</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">{{ sub.description || '-' }}</p>
                            </div>
                            <Button @click="router.push('/team/' + (sub.id || sub._id))" type="secondary" class="!m-0 text-xs">
                                {{ $t('table.edit') }}
                            </Button>
                        </div>
                    </div>
                </FormContainer>
                <FormContainer v-if="teamId">
                    <TeamUserSearch 
                        ref="teamUserSearch"
                        @user-selected="handleUserSelected"
                        :exclude="form.members.map(m => m.id) || []"
                    />
                </FormContainer>
                <!-- Team Members List -->
                <FormContainer v-if="teamId">
                    <TeamMembersList 
                        :members="form.members"
                        :teamId="teamId"
                        :ownerId="teamOwnerId"
                        :canManage="canManageTeam"
                        @member-removed="removeMember"
                    />
                </FormContainer>

            </form>
    </CreateEditData>
</template>
<script setup>
import { reactive, ref, computed, watch } from 'vue';
import toastStore from '@/store/toast.store'
import userStore, { verifyIam } from '@/store/user.store'
import dialogStore from '@/store/dialog.store'
import {debounce} from '@/service/debounce'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { TrashIcon, ArrowPathIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'
import Tooltip from '@/components/common/elements/TriadTooltip.vue'
import TeamUserSearch from '@/components/team/TeamUserSearch.vue'
import TeamMembersList from '@/components/team/TeamMembersList.vue'
import { useTour } from '@/composables/useTour'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { startTeamFormTour } = useTour()
const teamId = computed(() => route.params.id)

const loading = ref(false)
const teamOwnerId = ref(null)
const teamUserSearch = ref(null)

const canManageTeam = computed(() => {
  try {
    const me = userStore.state.userInfo;
    if (me && teamOwnerId.value && String(me.id) === String(teamOwnerId.value)) return true;
    return verifyIam(me, '/team');
  } catch {
    return false;
  }
});
const similarTeams = ref([])
const allTeams = ref([])
const subgroups = ref([])

const form = reactive({
  name: '',
  description: '',
  parentId: null,
  members: []
})

const availableParentTeams = computed(() => {
  return allTeams.value.filter(t => {
    const id = t.id || t._id;
    return id !== teamId.value;
  });
})

const fetchAllTeams = async () => {
  try {
    const response = await fetch('/api/team?size=100');
    if (response.ok) {
      const data = await response.json();
      allTeams.value = Array.isArray(data) ? data : (data.data || []);
    }
  } catch (e) {
    console.error('Failed to fetch teams list', e);
  }
};

watch(() => route.params.id, (newId) => {
  fetchAllTeams();
  if (newId) {
    getTeam()
  } else {
    form.name = ''
    form.description = ''
    form.parentId = null
    form.members = []
    teamOwnerId.value = null
    subgroups.value = []
  }
}, { immediate: true })

const handleUserSelected = async (user) => {
  if (!form.members.find(m => m.id === user.id)) {
    const response = await fetch(`/api/team/${teamId.value}/member`, {
      method: 'POST',
      body: JSON.stringify({ email: user.email }),
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (response.status === 200) {
      form.members.push(user);
      teamUserSearch.value?.clearSearch();
      toastStore.dispatch('addMessage', {
        message: 'User added to team',
        icon: 'success',
        ttl: 3000
      });
    } else {
      toastStore.dispatch('addMessage', {
        message: 'Error adding user to team',
        icon: 'error',
        ttl: 3000
      });
    }
  }
}

const removeMember = async (member) => {
    const response = await fetch(`/api/team/${teamId.value}/member/${member.id}`, {
      method: 'DELETE'
    })
    if (response.status === 200) {
      form.members = form.members.filter(m => m.id !== member.id);
      toastStore.dispatch('addMessage', {
          message: 'User removed from team',
          icon: 'success',
          ttl: 3000
      });
    } else {
      toastStore.dispatch('addMessage', {
          message: 'Error removing user from team',
          icon: 'error',
          ttl: 3000
      });
    }
}

const handleSubmit = async () => {
  try {
    if (!validateForm()) {
      toastStore.dispatch('addMessage', {
        message: 'Please fill in all fields',
        icon: 'error',
        ttl: 3000
      })
      return;
    }
    let response
    if (teamId.value) {
      response = await fetch('/api/team/'+teamId.value, {
        method: 'PUT',
        body: JSON.stringify(form),
        headers: {
          'Content-Type': 'application/json'
        }
      })
    } else{
      response = await fetch('/api/team/', {
        method: 'POST',
        body: JSON.stringify(form),
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }
    
    if (response.status === 200) {
        const data = await response.json();
        toastStore.dispatch('addMessage', {
            message: 'Team saved',
            icon: 'success',
            ttl: 3000
        })
        if (!teamId.value && (data.id || data._id)) {
            router.push('/team/' + (data.id || data._id));
        } else {
            resetForm();
        }
    } else {
        let isLimitReached = false;
        try {
            const errorData = await response.clone().json();
            if (errorData && (errorData.error === 'LIMIT_REACHED' || errorData.message === 'LIMIT_REACHED')) {
                isLimitReached = true;
            }
        } catch (e) {
            // ignore
        }
        
        if (!isLimitReached) {
            toastStore.dispatch('addMessage', {
                message: 'Error saving team',
                icon: 'error',
                ttl: 3000
            })
        }
    }
  } catch (error) {
    console.error('Error saving team:', error)
  }
}

const confirmDelete = async () => {
    if (await dialogStore.dispatch('confirm', t('form.deleteConfirm'))) {
        try {
            const response = await fetch(`/api/team/${teamId.value}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                toastStore.dispatch('addMessage', { message: 'Team deleted', icon: 'success' });
                router.push('/team');
            } else {
                toastStore.dispatch('addMessage', { message: 'Error deleting team', icon: 'error' });
            }
        } catch (error) {
            console.error('Error deleting team:', error);
            toastStore.dispatch('addMessage', { message: 'Error deleting team', icon: 'error' });
        }
    }
}

const validateForm = () => {
  return form.name
}

const resetForm = () => {
  if (teamId.value){
    getTeam()
  } else {
    form.name = ''
    form.description = ''
    form.parentId = null
    form.members = []
    subgroups.value = []

    toastStore.dispatch('addMessage', {
      message: 'Form reseted',
      icon: 'success',
      ttl: 3000
    })
  }
}

const searchSimilarTeams = debounce(async (ev) => {
  if(teamId.value) {
    return;
  }
  toastStore.dispatch('addMessage', {
      message: 'Searching for similar team',
      icon: 'info',
      ttl: 1000
  })
  const response = await fetch(`/api/team/search?q=${ev.target.value.replace(/[\r\n]+/g, ' ')}&exclude=`);
  if (response.status === 200) {
    const data = await response.json()
    similarTeams.value = data.map( (s) => {
        return {
            ...s,
            name: s.name
        }
    })
    if (similarTeams.value.length > 0) {
        toastStore.dispatch('addMessage', {
            message: similarTeams.value.length + ' similar team' + (similarTeams.value.length > 1 ? 's' : '') + ' found',
            icon: 'success',
            ttl: 5000
        })
    } else {
        toastStore.dispatch('addMessage', {
            message: 'No similar team name found',
            icon: 'info',
            ttl: 3000
        })
    }
  } else {
    similarTeams.value = [];
    toastStore.dispatch('addMessage', {
      message: 'Error searching for teams',
      icon: 'error',
      ttl: 5000
    })
  }
}, 1000)

async function getTeam() {
  loading.value = true;
  const response = await fetch(`/api/team/${teamId.value}`)
  if (!response.ok) {
    // Backend negou (404 sem acesso / 403): volta para a lista em vez de
    // abrir o formulário vazio para um recurso alheio (IDOR via URL direta).
    toastStore.dispatch('addMessage', { message: t('team.notAuthorized'), icon: 'warning', ttl: 3000 });
    loading.value = false;
    router.push('/team');
    return;
  }
  const data = await response.json();
  teamOwnerId.value = data.userId || data.user_id || null;
  loading.value = false;
  form.name = data.name
  form.description = data.description;
  form.parentId = data.parentId || data.parent_id || null;
  form.members = data.members || [];
  subgroups.value = data.subgroups || [];
}
</script> 